// src/data/extensionRequests.js
//
// T5 item 6 (2026-10-15) - RULINGS 133 AND 134: THE MEMBER RECORDS HIS OWN REQUEST TO EXTEND A TIME LIMIT.
// Ruling 133 (data\YCP_RULINGS_2026-09-11.md:13): the member may seek an extension without representation UP TO THE
// OWNERSHIP THRESHOLD; the caution ("not recommended") travels with the right, every time it is offered, never as a
// block; past the threshold the extension goes through the representative holding the matter. Ruling 134 (:19): the
// request is timely on the member's OWN ACT - written, in compliance with the controlling agreement, before expiry,
// substantively proven (timestamp, receipts) - and the carrier's answer neither validates nor defeats it.
// The threshold is carriage (ruling 135, carriage.js): while carriedBy is the representative, the member does not
// record one here - he is told to work with his representative.
//
// claims/{id}/extensionRequests/{auto}, written ONLY by the extensionRequest Cloud Function. Append-only. The office
// holding the claim and the General Chairperson's office are told (the Organization must know of a clock move).
// PURE; projected into functions\shared\ by scripts\sync-shared-modules.js.
import { factsClaimant } from './factsActs';

export const MEANS = ['email', 'certified_mail', 'hand_delivery', 'fax', 'other'];
export const MEANS_LABELS = { email: 'E-mail', certified_mail: 'Certified mail', hand_delivery: 'Hand delivery', fax: 'Fax', other: 'Other' };
export const TEXT_MAX = 2000;
export const NOTICE_KIND = 'extension_requested';

const s = (v) => (v == null ? '' : String(v)).trim();
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(s(v)) && !Number.isNaN(Date.parse(s(v) + 'T00:00:00Z'));

// Past the ownership threshold? (the representative carries the claim)
export function pastThreshold(claim) {
  return s(claim && claim.carriedBy) === 'representative';
}

// Who may record: the claimant (factsActs' stricter test), and only below the threshold.
export function mayRequest(claim, caller) {
  const who = factsClaimant(claim, caller);
  if (!who.may) return who;
  if (pastThreshold(claim)) {
    return { may: false, because: 'Your representative now carries this claim, so an extension now goes through the representative holding it - ask ' + (s(claim.carriedByName) || 'your representative') + '.' };
  }
  return { may: true };
}

// input = { sentOn, sentTo, means, timeLimit, expiresOn, proof, text }
export function validateRequest(input, claim) {
  const i = input || {};
  const problems = [];
  const v = {
    sentOn: s(i.sentOn), sentTo: s(i.sentTo), means: s(i.means), timeLimit: s(i.timeLimit),
    expiresOn: s(i.expiresOn), proof: s(i.proof), text: s(i.text),
  };
  if (!isDate(v.sentOn)) problems.push('Give the date you sent the written request.');
  if (!v.sentTo) problems.push('Name the carrier officer you sent it to.');
  if (MEANS.indexOf(v.means) === -1) problems.push('Say how you sent it.');
  if (!v.timeLimit) problems.push('Name the time limit you asked to extend.');
  if (!isDate(v.expiresOn)) problems.push('Give the last day of that time limit.');
  if (!v.proof) problems.push('Describe your proof of sending - the timestamp, receipt or tracking number. A request must be provable.');
  if (v.text.length > TEXT_MAX || v.proof.length > TEXT_MAX) problems.push('The text is longer than ' + TEXT_MAX + ' characters.');
  const st = s(claim && claim.status);
  if (!st || st === 'draft') problems.push('A draft has no time limit running yet.');
  return { ok: problems.length === 0, problems, value: v };
}

// Ruling 134: timely on his own act - sent on or before the last day. The carrier's answer is not an input.
export function timeliness(v) {
  if (!isDate(v.sentOn) || !isDate(v.expiresOn)) return { timely: null, line: 'Timeliness cannot be read without both dates.' };
  const timely = v.sentOn <= v.expiresOn;
  return { timely, line: timely
    ? 'Sent ' + v.sentOn + ', on or before the last day (' + v.expiresOn + '): on time on your own act. The carrier\'s answer does not change that.'
    : 'Sent ' + v.sentOn + ', AFTER the last day (' + v.expiresOn + '): the request was not made before the time limit expired.' };
}

export function planRequest({ claim, claimId, input, caller, at }) {
  const c = claim || {};
  const t = timeliness(input);
  const label = s(c.claimNumber) || s(c.title) || s(claimId).slice(0, 8).toUpperCase();
  const who = s(caller && caller.name) || s(c.claimantName) || 'The claimant';
  const record = Object.assign({}, input, { byUid: s(caller && caller.uid), byName: who, timely: t.timely });
  const body = who + ' recorded a written request to extend "' + input.timeLimit + '" on claim ' + label + ': sent ' + input.sentOn
    + ' to ' + input.sentTo + ' by ' + (MEANS_LABELS[input.means] || input.means) + '; the limit\'s last day ' + input.expiresOn + '. ' + t.line
    + ' Proof: ' + input.proof + '.';
  const common = { kind: NOTICE_KIND, claimId: s(claimId), committee: s(c.committee), scopeKey: s(c.scopeKey), recipientUid: null,
    channel: 'in_app', title: 'Extension requested by the claimant: ' + label, body, actorName: who, actorRole: 'claimant',
    createdAt: s(at) || new Date(0).toISOString(), readAt: null };
  const notices = [];
  const unaddressed = [];
  if (s(c.scopeKey) && !/\|UNROUTED$/.test(s(c.scopeKey))) notices.push(Object.assign({}, common, { audience: 'local_chair' }));
  else unaddressed.push('local_chair: the claim is not routed to a Local Chairperson');
  if (s(c.committee)) notices.push(Object.assign({}, common, { audience: 'general_chair' }));
  else unaddressed.push('general_chair: the claim carries no committee');
  return { record, notices, timeliness: t, unaddressed };
}
